import { isNotPastDate } from '@fukuri-admins/utils/common/date';
import { z } from 'zod';

/**
 * 利用者追加フォームのバリデーションスキーマ
 *
 * パスワードの制約は旧 AddUserModal の validatePassword と互換
 * - 8文字以上
 * - 英数字混合
 *
 * billableFrom は常に必須（利用開始日フィールドは常時表示のため、必須バリデーションも常に適用する）。
 * 引数を取らないため関数ではなく定数として定義する。
 */
export const userFormSchema = z
  .object({
    clientId: z.string().min(1, '利用企業を選択してください。'),
    fullName: z.string().min(1, '氏名が入力されていません。'),
    email: z
      .string()
      .min(1, 'メールアドレスが入力されていません。')
      .regex(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        'メールアドレスの形式が正しくありません。'
      ),
    externalUserId: z.string().optional(),
    password: z
      .string()
      .min(8, 'パスワードは8文字以上入力してください。')
      .regex(/[a-zA-Z]/, 'パスワードは英数字混合にしてください。')
      .regex(/[0-9]/, 'パスワードは英数字混合にしてください。'),
    passwordConfirmation: z.string().min(1, 'パスワード確認が入力されていません。'),
    // .refine(v => v !== null) は実行時チェックを追加するだけで billableFrom の型を
    // Date に絞り込まない（Zod の型推論は refine の述語からは narrow されない）ため、
    // .transform で明示的に Date へキャストし、以降（generateRequest 等）で
    // billableFrom が null を考慮した分岐を書かずに済むようにする。
    billableFrom: z
      .date({ invalid_type_error: '利用開始日を選択してください。' })
      .nullable()
      .refine(v => v !== null, { message: '利用開始日を選択してください。' })
      // Presentation の `fromDate: new Date()` はレンダー時点の固定値のため、フォームを
      // 開いたまま深夜0時をまたぐとカレンダー上で過去日が選択可能なまま残り得る。
      // この refine は送信時に再評価されるため、そのズレによる過去日を弾く。
      .refine(isNotPastDate, { message: '過去の日付は選択できません。' })
      .transform(v => v as Date),
  })
  .refine(data => data.password === data.passwordConfirmation, {
    message: 'パスワードとパスワード（再入力）が一致しません。',
    path: ['passwordConfirmation'],
  });

export type CreateUserFormFieldValues = z.infer<typeof userFormSchema>;
